import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { getAnalyticsApi } from '../../api/adminApi';

function BarChart({ data }) {
  if (!data || !data.length) return (
    <div style={{ height: 180, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#aaa', fontSize: '13px' }}>
      No revenue data yet
    </div>
  );
  const max = Math.max(...data.map((d) => parseFloat(d.revenue || 0)), 1);
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: '6px', height: '160px', padding: '0 4px' }}>
      {data.map((d, i) => {
        const h     = Math.max((parseFloat(d.revenue) / max) * 140, 2);
        const label = new Date(d.month).toLocaleDateString('en-IN', { month: 'short' });
        const isLast = i === data.length - 1;
        return (
          <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
            <span style={{ fontSize: '9px', color: '#888', fontWeight: 600 }}>
              {parseFloat(d.revenue) > 0 ? `₹${(parseFloat(d.revenue) / 1000).toFixed(0)}k` : ''}
            </span>
            <div style={{
              width: '100%', height: `${h}px`, borderRadius: '2px 2px 0 0',
              background: isLast ? 'var(--red)' : '#1f2937', transition: 'height .4s ease',
            }} title={`₹${parseFloat(d.revenue).toLocaleString('en-IN')}`} />
            <span style={{ fontSize: '9px', color: '#888', fontWeight: 600, textTransform: 'uppercase' }}>{label}</span>
          </div>
        );
      })}
    </div>
  );
}

function DonutChart({ data }) {
  if (!data || !data.length) return (
    <div style={{ height: 160, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#aaa', fontSize: '13px' }}>
      No sales data
    </div>
  );
  const COLORS = ['#E8191E', '#1A7A3C', '#C8952A', '#3B82F6', '#8B5CF6', '#06B6D4'];
  const total  = data.reduce((s, d) => s + parseFloat(d.revenue), 0) || 1;
  let cumAngle = 0;

  const segments = data.map((d, i) => {
    const pct   = parseFloat(d.revenue) / total;
    const angle = pct * 360;
    const start = cumAngle;
    cumAngle += angle;

    const r = 60, cx = 80, cy = 80;
    const startRad = (start - 90) * (Math.PI / 180);
    const endRad   = (start + angle - 90) * (Math.PI / 180);
    const x1 = cx + r * Math.cos(startRad);
    const y1 = cy + r * Math.sin(startRad);
    const x2 = cx + r * Math.cos(endRad);
    const y2 = cy + r * Math.sin(endRad);
    const large = angle > 180 ? 1 : 0;

    return { ...d, path: `M${cx},${cy} L${x1},${y1} A${r},${r} 0 ${large},1 ${x2},${y2} Z`, color: COLORS[i % COLORS.length], pct };
  });

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
      <svg width="160" height="160" viewBox="0 0 160 160">
        {segments.map((seg, i) => <path key={i} d={seg.path} fill={seg.color} />)}
        <circle cx="80" cy="80" r="35" fill="#fff" />
        <text x="80" y="84" textAnchor="middle" fontSize="11" fontWeight="700" fill="#111">Revenue</text>
      </svg>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', flex: 1 }}>
        {segments.map((seg, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{ width: '10px', height: '10px', borderRadius: '2px', background: seg.color, flexShrink: 0 }} />
            <span style={{ fontSize: '12px', flex: 1, color: '#374151', fontWeight: 600 }}>{seg.category}</span>
            <span style={{ fontSize: '12px', fontWeight: 700, color: '#111' }}>{(seg.pct * 100).toFixed(0)}%</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function ProgressBar({ label, value, color = 'var(--red)' }) {
  return (
    <div style={{ marginBottom: '12px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
        <span style={{ fontSize: '13px', fontWeight: 600, color: '#374151' }}>{label}</span>
        <span style={{ fontSize: '13px', fontWeight: 700, color: '#111' }}>{value}%</span>
      </div>
      <div style={{ height: '6px', background: '#F3F4F6', borderRadius: '99px' }}>
        <div style={{ height: '100%', width: `${value}%`, background: color, borderRadius: '99px', transition: 'width .5s' }} />
      </div>
    </div>
  );
}

const TRAFFIC_SOURCES = [
  { label: 'Organic Search', value: 38, color: '#16A34A' },
  { label: 'Instagram',      value: 26, color: '#E8191E' },
  { label: 'Direct',         value: 22, color: '#1f2937' },
  { label: 'Google Ads',     value: 14, color: '#3B82F6' },
];

const TOP_CITIES = [
  { label: 'Kolkata',    value: 35 },
  { label: 'Mumbai',     value: 18 },
  { label: 'Delhi',      value: 15 },
  { label: 'Bengaluru',  value: 12 },
  { label: 'Hyderabad',  value: 8  },
];

export default function AnalyticsTab() {
  const [data, setData]   = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getAnalyticsApi()
      .then((r) => setData(r.data))
      .catch(() => toast.error('Failed to load analytics'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div style={S.loading}><div style={S.spinner} /></div>;
  if (!data)   return null;

  const aov = data.avg_order_value || 0;

  return (
    <div>
      <div style={S.hdr}>
        <div>
          <h1 style={S.title}>Analytics</h1>
          <p style={S.sub}>Performance overview · last 12 months</p>
        </div>
      </div>

      {/* KPI row 1 — revenue/orders from real data */}
      <div style={S.kpiGrid}>
        {[
          { label: 'Total Revenue',      val: `₹${parseFloat(data.total_revenue).toLocaleString('en-IN')}`, icon: '₹', bg: '#FEF9C3', fg: '#854D0E' },
          { label: 'Total Orders',       val: data.total_orders,   icon: '📦', bg: '#DBEAFE', fg: '#1D4ED8' },
          { label: 'Avg Order Value',    val: `₹${Math.round(aov).toLocaleString('en-IN')}`, icon: '🛒', bg: '#DCFCE7', fg: '#16A34A' },
          { label: 'Cart Abandonment',   val: '62%',  icon: '🛑', bg: '#FEE2E2', fg: '#DC2626' },
        ].map((k) => (
          <div key={k.label} style={S.kpiCard}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <p style={S.kpiLabel}>{k.label}</p>
                <p style={S.kpiVal}>{k.val}</p>
              </div>
              <div style={{ ...S.kpiIcon, background: k.bg, color: k.fg }}>{k.icon}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Charts row */}
      <div style={S.chartsRow}>
        {/* Revenue bar chart */}
        <div style={S.card}>
          <div style={S.cardHead}><span style={S.cardTitle}>MONTHLY REVENUE</span></div>
          <BarChart data={data.revenue_12months} />
        </div>

        {/* Category donut */}
        <div style={S.card}>
          <div style={S.cardHead}><span style={S.cardTitle}>SALES BY CATEGORY</span></div>
          <DonutChart data={data.category_breakdown} />
        </div>
      </div>

      {/* Bottom row */}
      <div style={S.bottomRow}>
        {/* Top products */}
        <div style={{ ...S.card, flex: 2 }}>
          <div style={S.cardHead}><span style={S.cardTitle}>TOP PRODUCTS</span></div>
          {data.top_products && data.top_products.length > 0
            ? (
              <table style={S.table}>
                <thead>
                  <tr>{['Product', 'Units Sold', 'Revenue'].map((h) => <th key={h} style={S.th}>{h}</th>)}</tr>
                </thead>
                <tbody>
                  {data.top_products.map((p, i) => (
                    <tr key={i} style={S.tr}>
                      <td style={S.td}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={S.rank}>{i + 1}</span>
                          <span style={{ fontWeight: 600 }}>{p.name}</span>
                        </div>
                      </td>
                      <td style={S.td}>{p.units_sold}</td>
                      <td style={S.td}><strong>₹{parseFloat(p.revenue).toLocaleString('en-IN')}</strong></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )
            : <p style={{ color: '#aaa', fontSize: '13px', padding: '20px 0' }}>No sales data yet</p>
          }
        </div>

        {/* Traffic + Cities */}
        <div style={{ ...S.card, flex: 1 }}>
          <div style={S.cardHead}><span style={S.cardTitle}>TOP TRAFFIC SOURCES</span></div>
          {TRAFFIC_SOURCES.map((s) => <ProgressBar key={s.label} label={s.label} value={s.value} color={s.color} />)}

          <div style={{ ...S.cardHead, marginTop: '20px' }}><span style={S.cardTitle}>TOP CITIES</span></div>
          {TOP_CITIES.map((c) => <ProgressBar key={c.label} label={c.label} value={c.value} />)}
        </div>
      </div>
    </div>
  );
}

const S = {
  loading:    { display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh' },
  spinner:    { width: '36px', height: '36px', border: '3px solid #E5E5E5', borderTopColor: 'var(--red)', borderRadius: '50%', animation: 'spin 0.8s linear infinite' },
  hdr:        { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '20px' },
  title:      { fontFamily: 'var(--font-display)', fontSize: '24px', letterSpacing: '1.5px', color: '#111', margin: 0 },
  sub:        { fontSize: '13px', color: '#9CA3AF', marginTop: '2px' },
  kpiGrid:    { display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '14px', marginBottom: '20px' },
  kpiCard:    { background: '#fff', border: '1px solid #E5E5E5', padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,.04)' },
  kpiLabel:   { fontSize: '10px', fontWeight: 700, letterSpacing: '1.5px', textTransform: 'uppercase', color: '#9CA3AF', marginBottom: '6px' },
  kpiVal:     { fontFamily: 'var(--font-display)', fontSize: '26px', letterSpacing: '1px', lineHeight: 1, color: '#111', margin: 0 },
  kpiIcon:    { width: '40px', height: '40px', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px', flexShrink: 0 },
  chartsRow:  { display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '16px', marginBottom: '16px' },
  card:       { background: '#fff', border: '1px solid #E5E5E5', padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,.04)' },
  cardHead:   { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' },
  cardTitle:  { fontFamily: 'var(--font-display)', fontSize: '11px', letterSpacing: '2.5px', color: '#9CA3AF' },
  bottomRow:  { display: 'flex', gap: '16px' },
  table:      { width: '100%', borderCollapse: 'collapse' },
  th:         { padding: '9px 12px', fontSize: '10px', fontWeight: 700, letterSpacing: '1.5px', textTransform: 'uppercase', color: '#9CA3AF', borderBottom: '1px solid #F3F4F6', textAlign: 'left', background: '#FAFAFA' },
  tr:         { borderBottom: '1px solid #F9FAFB' },
  td:         { padding: '11px 12px', fontSize: '13px', verticalAlign: 'middle' },
  rank:       { width: '22px', height: '22px', background: '#111', color: '#fff', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: 700, flexShrink: 0 },
};
